import api from './axiosApi'
import type { PlanItem } from './planItems'

// Types from OpenAPI spec
export type ProcessingStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'
export type ProductListStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'

export interface ProductListCreateRequest {
  file_id: string
  name: string
  description?: string | null
}

export interface ProcessingResponse {
  processing_id: string
  resource_id: string
  status: ProcessingStatus
  events_url: string
}

export interface ProductListRead {
  id: string
  name: string
  description: string | null
  status: ProductListStatus
  file_id: string | null
  total_rows: number
  valid_rows: number
  invalid_rows: number
  skipped_rows: number
  error_report_file_id: string | null
  created_by: string
  created_at: string
  updated_at: string
}

export type ValidationErrorSeverity = 'error' | 'warning'

export type ValidationErrorCode =
  | 'REQUIRED'
  | 'MISSING_IDENTIFIER'
  | 'INVALID_INTEGER'
  | 'INVALID_DECIMAL'
  | 'ROUNDED_INTEGER'
  | 'MIN_VALUE'
  | 'MAX_VALUE'
  | 'MAX_LENGTH'
  | 'IMAGE_NOT_FOUND'

export interface ValidationError {
  column: string
  value: string | null
  code: ValidationErrorCode
  message: string
  severity: ValidationErrorSeverity
}

export interface ProductListItemRead {
  id: string
  product_list_id: string
  row_number: number
  is_valid: boolean
  raw_data: Record<string, unknown> | null
  parsed_data: {
    sku_number?: string
    description?: string
    [key: string]: unknown
  } | null
  enrichment_sources: Record<string, string> | null
  errors: ValidationError[] | null
  created_at: string
}

export interface ErrorReportResponse {
  download_url: string
}

export interface PaginationMeta {
  skip: number
  limit: number
  total: number
  has_more: boolean
}

export interface PaginatedProductListItems {
  data: ProductListItemRead[]
  pagination: PaginationMeta
}

/**
 * Create a product list from an uploaded CSV file
 * POST /product-lists
 * Returns 202 with processing info for SSE subscription
 */
export async function createProductList(params: {
  fileId: string
  name: string
  description?: string
}): Promise<ProcessingResponse> {
  const response = await api.post<ProcessingResponse>('/product-lists', {
    file_id: params.fileId,
    name: params.name,
    description: params.description ?? null,
  } satisfies ProductListCreateRequest)
  return response.data
}

/**
 * Get a product list by ID
 * GET /product-lists/{product_list_id}
 */
export async function getProductList(
  productListId: string
): Promise<ProductListRead> {
  const response = await api.get<ProductListRead>(
    `/product-lists/${productListId}`
  )
  return response.data
}

/**
 * Get items from a product list with pagination
 * GET /product-lists/{product_list_id}/items
 *
 * @param productListId - The product list ID
 * @param page - Page number (1-indexed for frontend convenience)
 * @param pageSize - Items per page
 * @param isValid - Optional filter for valid/invalid items
 */
export async function getProductListItems(
  productListId: string,
  page: number = 1,
  pageSize: number = 100,
  isValid?: boolean
): Promise<PaginatedProductListItems> {
  // Convert 1-indexed page to offset
  const skip = (page - 1) * pageSize

  const params = new URLSearchParams({
    skip: skip.toString(),
    limit: pageSize.toString(),
  })

  if (isValid !== undefined) {
    params.append('is_valid', isValid.toString())
  }

  const response = await api.get<PaginatedProductListItems>(
    `/product-lists/${productListId}/items?${params.toString()}`
  )
  return response.data
}

/**
 * Map a ProductListItemRead to frontend PlanItem type
 */
export function mapProductListItemToPlanItem(
  item: ProductListItemRead
): PlanItem {
  return {
    id: item.id,
    skuNumber: item.parsed_data?.sku_number ?? '',
    description: item.parsed_data?.description ?? '',
  }
}

/**
 * Get the error report CSV download URL for a product list
 * GET /product-lists/{product_list_id}/errors
 *
 * Returns 409 if not yet completed, 404 if no errors
 */
export async function getProductListErrorReport(
  productListId: string
): Promise<ErrorReportResponse> {
  const response = await api.get<ErrorReportResponse>(
    `/product-lists/${productListId}/errors`
  )
  return response.data
}

/**
 * Fetch product list items and map to PlanItem format
 * Convenience function for the wizard
 */
export async function getProductListItemsAsPlanItems(
  productListId: string,
  page: number = 1,
  pageSize: number = 100
): Promise<{
  items: PlanItem[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}> {
  const response = await getProductListItems(
    productListId,
    page,
    pageSize,
    true
  )

  const items = response.data.map(mapProductListItemToPlanItem)
  const totalCount = response.pagination.total
  const totalPages = Math.ceil(totalCount / pageSize)

  return {
    items,
    totalCount,
    page,
    pageSize,
    totalPages,
  }
}
